import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Store, Camera, MessagesSquare, ShieldCheck, Rocket } from "lucide-react";

const SLIDES = [
  { icon: Store, title: "Welcome to SellStuff", body: "Dubai's smartest way to buy and sell second-hand.", color: "bg-primary" },
  { icon: Camera, title: "List in under 30 seconds", body: "Take a photo. Our AI detects your item, writes the listing and sets the price. No forms, no hassle.", color: "bg-blue-600" },
  { icon: MessagesSquare, title: "Never haggle again", body: "Max, our AI negotiation agent, handles all buyer offers on your behalf. You just wait for the sale.", color: "bg-indigo-600" },
  { icon: ShieldCheck, title: "Buy with confidence", body: "Payment is held securely until your item arrives. You have 24 hours to raise any concerns. Every seller is accountable.", color: "bg-emerald-600" },
  { icon: Rocket, title: "You're all set!", body: "Browse hundreds of listings or sell your first item today.", color: "bg-primary" },
];

export default function Onboarding({ onDone }) {
  const [i, setI] = useState(0);
  const S = SLIDES[i];
  const Icon = S.icon;
  const last = i === SLIDES.length - 1;

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-white" data-testid="onboarding-modal">
      {/* Skip — every screen except the last */}
      <div className="flex h-16 items-center justify-end px-6">
        {!last && (
          <button onClick={onDone} data-testid="onboarding-skip" className="rounded-full px-4 py-1.5 text-sm font-medium text-slate-500 hover:bg-slate-100">Skip</button>
        )}
      </div>

      <div className="flex flex-1 items-center justify-center px-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={i}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="w-full max-w-md text-center"
          >
            <div className={`mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-3xl ${S.color} text-white shadow-lg`}>
              <Icon className="h-12 w-12" />
            </div>
            <h1 data-testid="onboarding-title" className="font-heading text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">{S.title}</h1>
            <p className="mt-4 text-base text-slate-600 sm:text-lg">{S.body}</p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Progress dots */}
      <div className="flex items-center justify-center gap-2 pb-6" data-testid="onboarding-dots">
        {SLIDES.map((_, idx) => (
          <span key={idx} className={`h-2 rounded-full transition-all duration-300 ${idx === i ? "w-8 bg-primary" : "w-2 bg-slate-300"}`} />
        ))}
      </div>

      <div className="px-6 pb-10">
        {last ? (
          <Button onClick={onDone} data-testid="onboarding-lets-go" className="mx-auto flex w-full max-w-md items-center justify-center rounded-full bg-primary py-6 text-base font-bold hover:bg-primary/90">
            Let's Go
          </Button>
        ) : (
          <Button onClick={() => setI(i + 1)} data-testid="onboarding-next" className="mx-auto flex w-full max-w-md items-center justify-center rounded-full bg-primary py-6 text-base font-semibold hover:bg-primary/90">
            Next
          </Button>
        )}
      </div>
    </div>
  );
}
